import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
// Ember Data adapter 403 (from location.save()), distinct from the ajax-fetch
// ForbiddenError used by the routine-snapshot POST below.
import { ForbiddenError as AdapterForbiddenError } from '@ember-data/adapter/error';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type { DiffGroup, DiffItem } from 'garaje/components/hris-dashboard/save-confirm-modal';
import config from 'garaje/config/environment';
import { ForbiddenError } from 'garaje/services/ajax/errors';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type AuthzService from 'garaje/services/authz';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { Permission, PermissionType } from 'garaje/utils/ui-permissions';

interface StepDef {
  id: string;
  title: string;
  desc: string;
  icon: string;
  tone: 'red' | 'papaya' | 'gray';
  kind: 'perSystem' | 'org';
  sysLabel?: string;
  nestedIcon?: boolean;
  hasNotifyConfig?: boolean;
}

interface LocationDef {
  id: string;
  tag: string;
  acs: string[];
}

// `unconfigured` corresponds to backend `mode=UNCONFIGURED` on a drift-backfilled
// step: the registry gained a step the persisted snapshot doesn't cover, and the
// admin must pick a real mode before save (backend rejects unconfigured on write).
type Mode = 'live' | 'dryrun' | 'disabled' | 'unconfigured';

interface RoutineState {
  acs: Record<string, Mode>;
  blocklist: Mode;
  forceSignout: Mode;
  watchList: Mode;
}

const STEPS: StepDef[] = [
  {
    id: 'acs',
    title: 'Deprovision ACS credentials',
    desc: 'Remove employee access in access control systems.',
    icon: 'suspicious-activity-hris',
    tone: 'red',
    kind: 'perSystem',
    sysLabel: 'Access control systems',
  },
  {
    id: 'watchList',
    title: 'Add to watch list',
    desc: "Flag the terminated employee's identity so a return visit alerts the front desk.",
    icon: 'view-only-eyeball',
    tone: 'red',
    kind: 'org',
    hasNotifyConfig: true,
  },
  {
    id: 'blocklist',
    title: 'Add to visitor blocklist',
    desc: 'Prevent deactivated employees from visiting.',
    icon: 'dash-in-circle-hris',
    tone: 'red',
    kind: 'org',
  },
  {
    id: 'forceSignout',
    title: 'Force sign-out',
    desc: 'Force the employee to sign out of all locations.',
    icon: 'door-hris',
    tone: 'papaya',
    kind: 'org',
  },
];

const GRACE_UNIT_HOURS: Record<string, number> = {
  minutes: 1 / 60,
  hours: 1,
  days: 24,
};

const GRACE_MAX_HOURS = 5 * 24;

const EMPTY_STATE: RoutineState = {
  acs: {},
  blocklist: 'disabled',
  forceSignout: 'disabled',
  watchList: 'disabled',
};

function toUiMode(apiMode: string): Mode {
  if (apiMode === 'LIVE') return 'live';
  if (apiMode === 'SIMULATE') return 'dryrun';
  if (apiMode === 'UNCONFIGURED') return 'unconfigured';
  return 'disabled';
}

function toApiMode(mode: Mode): string {
  if (mode === 'live') return 'LIVE';
  if (mode === 'dryrun') return 'SIMULATE';
  if (mode === 'unconfigured') return 'UNCONFIGURED';
  return 'DISABLED';
}

function cloneState(s: RoutineState): RoutineState {
  return { ...s, acs: { ...s.acs } };
}

export default class LocationOverviewHrisDashboardController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare authz: AuthzService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  @tracked eventId = 'terminate';
  @tracked gracePeriodQty = 24;
  @tracked gracePeriodUnit: 'minutes' | 'hours' | 'days' = 'hours';
  @tracked routineState: RoutineState = cloneState(EMPTY_STATE);
  @tracked locations: LocationDef[] = [];
  @tracked showSaveConfirmModal = false;
  @tracked pendingDiffGroups: DiffGroup[] = [];
  @tracked showNotifyModal = false;
  @tracked isSaving = false;
  // Inline error for the envoy-web watch-list contact save (e.g. a 403). Rendered
  // next to the notify editor, not as a flash, so a permission failure is visible.
  @tracked watchlistContactError = '';

  private _savedGraceQty = 24;
  private _savedGraceUnit: 'minutes' | 'hours' | 'days' = 'hours';
  private _savedState: RoutineState = cloneState(EMPTY_STATE);

  loadFromSnapshot(snapshot: unknown, unauthorized = false): void {
    // Controllers are singletons: a modal left open when the user navigated away
    // would still render open on route re-entry.
    this.showNotifyModal = false;
    this.showSaveConfirmModal = false;
    this.pendingDiffGroups = [];

    if (unauthorized) {
      this.flashMessages.showAndHideFlash('error', 'You are not authorized to view this configuration.');
      return;
    }

    if (!snapshot) return;

    const snap = snapshot as {
      config: {
        grace_period_seconds: number;
        steps: Array<{
          step_type: string;
          mode?: string;
          default?: boolean;
          per_integration_location?: Array<{
            integration_type: string;
            location_id: number;
            location_name?: string;
            mode: string;
          }>;
        }>;
      };
    };

    const secs = snap.config.grace_period_seconds;
    if (secs % 86400 === 0) {
      this.gracePeriodQty = secs / 86400;
      this.gracePeriodUnit = 'days';
    } else if (secs % 3600 === 0) {
      this.gracePeriodQty = secs / 3600;
      this.gracePeriodUnit = 'hours';
    } else {
      this.gracePeriodQty = Math.round(secs / 60);
      this.gracePeriodUnit = 'minutes';
    }

    const acsStep = snap.config.steps.find((s) => s.step_type === 'LOCATION_ACS_DEPROVISION');
    const blocklistStep = snap.config.steps.find((s) => s.step_type === 'COMPANY_BLOCKLIST_ADD');
    const forceSignoutStep = snap.config.steps.find((s) => s.step_type === 'COMPANY_FORCE_SIGNOUT');
    const watchListStep = snap.config.steps.find((s) => s.step_type === 'COMPANY_WATCHLIST_ADD');

    const locMap = new Map<string, LocationDef>();
    acsStep?.per_integration_location?.forEach((item) => {
      const locId = String(item.location_id);
      // eslint-disable-next-line ember/use-ember-get-and-set
      if (!locMap.has(locId)) locMap.set(locId, { id: locId, tag: item.location_name ?? locId, acs: [] });
      // eslint-disable-next-line ember/use-ember-get-and-set
      locMap.get(locId)!.acs.push(item.integration_type);
    });
    this.locations = [...locMap.values()];

    const acs: Record<string, Mode> = {};
    acsStep?.per_integration_location?.forEach((item) => {
      acs[`${item.location_id}|${item.integration_type}`] = toUiMode(item.mode);
    });

    this.routineState = {
      acs,
      blocklist: toUiMode(blocklistStep?.mode ?? 'DISABLED'),
      forceSignout: toUiMode(forceSignoutStep?.mode ?? 'DISABLED'),
      watchList: toUiMode(watchListStep?.mode ?? 'DISABLED'),
    };

    this._savedGraceQty = this.gracePeriodQty;
    this._savedGraceUnit = this.gracePeriodUnit;
    this._savedState = cloneState(this.routineState);
  }

  get isGlobal(): boolean {
    return this.abilities.can('manage all hris-dashboard');
  }

  get isReadOnly(): boolean {
    return !this.isGlobal && this.abilities.cannot('manage hris-dashboard');
  }

  private canManageLocation(locationId: string): boolean {
    if (this.isGlobal) return true;
    return this.authz.hasPermission(PermissionType.LOCATION, locationId, Permission.HRIS_LIFECYCLE_ROUTINE_MANAGE);
  }

  get graceInvalid(): boolean {
    const hours = this.gracePeriodQty * (GRACE_UNIT_HOURS[this.gracePeriodUnit] ?? 1);
    return hours > GRACE_MAX_HOURS;
  }

  get graceSummary(): string {
    return `${this.gracePeriodQty} ${this.gracePeriodUnit}`;
  }

  get isDirty(): boolean {
    if (this.gracePeriodQty !== this._savedGraceQty || this.gracePeriodUnit !== this._savedGraceUnit) {
      return true;
    }
    const s = this.routineState;
    const b = this._savedState;
    if (s.blocklist !== b.blocklist || s.forceSignout !== b.forceSignout) return true;
    if (s.watchList !== b.watchList) return true;
    for (const k of Object.keys(s.acs)) {
      if (s.acs[k] !== b.acs[k]) return true;
    }
    return false;
  }

  // A drift-backfilled step/slice lands with `mode=UNCONFIGURED`; the backend
  // rejects saves that still carry that sentinel for a slice the caller manages.
  // True only when there is an unconfigured step/slice the CURRENT USER CAN MANAGE
  // (and therefore must resolve before saving):
  //   - company org steps gate on `isGlobal` (manage-all — they own the PowerSelect)
  //   - per-location ACS entries gate on that row being editable (canManageLocation)
  // A step/slice the user can only read (locked) never blocks their save.
  get hasBlockingUnconfigured(): boolean {
    const s = this.routineState;
    const orgUnconfigured =
      s.watchList === 'unconfigured' || s.blocklist === 'unconfigured' || s.forceSignout === 'unconfigured';
    if (this.isGlobal && orgUnconfigured) {
      return true;
    }
    for (const [key, mode] of Object.entries(s.acs)) {
      if (mode !== 'unconfigured') continue;
      const locationId = key.split('|')[0]!;
      if (this.canManageLocation(locationId)) return true;
    }
    return false;
  }

  // The notify contact is the envoy-web watch-list contact (source of truth),
  // one shared company slot with Security → Watch List. It is NOT part of the
  // routine snapshot, so it is gated on and saved through the SAME watchlist
  // abilities/model the Security page uses — never the HRIS routine permissions.
  // Reads are null-safe: an absent location.watchlist reads as blank, still editable.
  get canReadWatchlistContact(): boolean {
    return this.abilities.can('read watchlist in security');
  }

  get canEditWatchlistContact(): boolean {
    return this.abilities.can('update watchlist in security');
  }

  get watchlistContactEmail(): string {
    return this.state.currentLocation?.watchlist?.notifyEmail ?? '';
  }

  get watchlistContactPhone(): string {
    return this.state.currentLocation?.watchlist?.notifyPhoneNumber ?? '';
  }

  get saveDisabled(): boolean {
    // Unconfigured steps/slices only block save when the current user can manage
    // them (see hasBlockingUnconfigured) — a locked/read-only slice never traps
    // them out of saving the slices they DO manage. The notify contact lives in
    // envoy-web and saves on its own (saveNotifyContacts), so it never gates the
    // routine-snapshot save here.
    return !this.isDirty || this.graceInvalid || this.hasBlockingUnconfigured || this.isSaving;
  }

  readonly graceUnitOptions: Array<{ value: string; label: string }> = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
  ];

  get selectedGraceUnit(): { value: string; label: string } | undefined {
    return this.graceUnitOptions.find((o) => o.value === this.gracePeriodUnit);
  }

  readonly modeOptions: Array<{ value: Mode; label: string }> = [
    { value: 'live', label: 'Live' },
    { value: 'dryrun', label: 'Simulate' },
    { value: 'disabled', label: 'Disabled' },
  ];

  private modeLabelFor(mode: Mode): string {
    if (mode === 'live') return 'Live';
    if (mode === 'dryrun') return 'Simulate';
    if (mode === 'unconfigured') return 'Select a mode';
    return 'Disabled';
  }

  private modePillClass(mode: Mode): string {
    if (mode === 'live') return 'bg-cilantro-10 text-cilantro-60';
    if (mode === 'dryrun') return 'bg-gem-10 text-gem-60';
    if (mode === 'unconfigured') return 'bg-papaya-10 text-papaya-50';
    return 'bg-gray-100 text-carbon-40';
  }

  private diffItem(label: string, fromMode: Mode, toMode: Mode): DiffItem {
    return {
      label,
      from: this.modeLabelFor(fromMode),
      fromPillClass: this.modePillClass(fromMode),
      to: this.modeLabelFor(toMode),
      toPillClass: this.modePillClass(toMode),
    };
  }

  private computeGoingLive(): DiffGroup[] {
    const cur = this.routineState;
    const base = this._savedState;
    const goingLive = (c: Mode, b: Mode): boolean => b !== 'live' && c === 'live';
    const byLoc: Record<string, DiffItem[]> = {};

    for (const loc of this.locations) {
      for (const vendor of loc.acs) {
        const k = `${loc.id}|${vendor}`;
        const c = cur.acs[k] ?? 'disabled';
        const b = base.acs[k] ?? 'disabled';
        if (goingLive(c, b)) {
          (byLoc[loc.id] ??= []).push(this.diffItem(`${vendor} — deprovision ACS credentials`, b, c));
        }
      }
    }

    const orgItems: DiffItem[] = [];
    for (const id of ['watchList', 'blocklist', 'forceSignout'] as const) {
      const c = cur[id];
      const b = base[id];
      if (goingLive(c, b)) {
        const step = STEPS.find((s) => s.id === id)!;
        orgItems.push(this.diffItem(step.title, b, c));
      }
    }

    const groups: DiffGroup[] = [];
    for (const loc of this.locations) {
      if (byLoc[loc.id]) groups.push({ heading: loc.tag, items: byLoc[loc.id]! });
    }
    if (orgItems.length) groups.push({ heading: 'Company-wide', items: orgItems });
    return groups;
  }

  private selectedOptionFor(mode: Mode): { value: Mode; label: string } | undefined {
    return this.modeOptions.find((o) => o.value === mode);
  }

  private iconBgFor(tone: StepDef['tone']): string {
    if (tone === 'papaya') return 'bg-papaya-10 text-papaya-40';
    if (tone === 'red') return 'bg-red-10 text-red-50';
    return 'bg-gray-100 text-carbon-40';
  }

  get stepsWithSubItems(): Array<
    StepDef & {
      iconBg: string;
      isLocked: boolean;
      isUnconfigured?: boolean;
      notifyEmail?: string;
      notifyPhone?: string;
      hasNotifyValues?: boolean;
      subItems?: Array<{
        key: string;
        label: string;
        locationPrefix?: string;
        isLast: boolean;
        isLocked: boolean;
        isUnconfigured: boolean;
        currentMode: Mode;
        modeLabel: string;
        modePillClass: string;
        selectedModeOption: { value: Mode; label: string } | undefined;
      }>;
      currentMode?: Mode;
      modeLabel?: string;
      modePillClass?: string;
      selectedModeOption?: { value: Mode; label: string } | undefined;
    }
  > {
    return STEPS.map((step) => {
      if (step.kind === 'perSystem') {
        const items = this.locations.flatMap((loc) =>
          loc.acs.map((vendor) => {
            const k = `${loc.id}|${vendor}`;
            const mode = this.routineState.acs[k] ?? 'disabled';
            return {
              key: k,
              label: vendor,
              locationPrefix: loc.tag,
              isLocked: !this.canManageLocation(loc.id),
              isUnconfigured: mode === 'unconfigured',
              currentMode: mode,
              modeLabel: this.modeLabelFor(mode),
              modePillClass: this.modePillClass(mode),
              selectedModeOption: this.selectedOptionFor(mode),
            };
          }),
        );
        const subItems = items.map((item, i) => ({ ...item, isLast: i === items.length - 1 }));
        return { ...step, iconBg: this.iconBgFor(step.tone), isLocked: this.isReadOnly, subItems };
      }

      const mode = this.routineState[step.id as 'watchList' | 'blocklist' | 'forceSignout'] ?? 'disabled';
      const isWatchList = step.id === 'watchList';
      return {
        ...step,
        iconBg: this.iconBgFor(step.tone),
        isLocked: !this.isGlobal,
        currentMode: mode,
        modeLabel: this.modeLabelFor(mode),
        modePillClass: this.modePillClass(mode),
        selectedModeOption: this.selectedOptionFor(mode),
        isUnconfigured: mode === 'unconfigured',
        notifyEmail: isWatchList ? this.watchlistContactEmail : undefined,
        notifyPhone: isWatchList ? this.watchlistContactPhone : undefined,
        hasNotifyValues: isWatchList ? Boolean(this.watchlistContactEmail || this.watchlistContactPhone) : false,
      };
    }).filter((step) => {
      // Org (company-scope) steps: show whenever the caller has company-read.
      // `visit global hris-dashboard` = `hris_lifecycle.routine.read` at company scope.
      // Row is rendered as a locked/read-only badge for read-only viewers, and as
      // a PowerSelect for company-manage callers via the existing isLocked/isReadOnly
      // wiring on the row. Backend `filterByReadScope` already drops org steps for
      // callers without company-read, so this filter matches the data shape.
      if (step.kind === 'org') return this.abilities.can('visit global hris-dashboard');
      return 'subItems' in step && step.subItems.length > 0;
    });
  }

  @action scrollToModes(): void {
    document.getElementById('about-modes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @action setEventId(id: string): void {
    this.eventId = id;
  }

  @action setGraceQty(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = parseInt(input.value || '0', 10);
    this.gracePeriodQty = Math.max(0, isNaN(val) ? 0 : val);
  }

  @action setGraceUnit(option: { value: string; label: string }): void {
    this.gracePeriodUnit = option.value as 'minutes' | 'hours' | 'days';
  }

  @action setMode(stepId: string, key: string | null, option: { value: Mode; label: string }): void {
    const s = this.routineState;
    const mode = option.value;
    if (stepId === 'acs' && key) {
      this.routineState = { ...s, acs: { ...s.acs, [key]: mode } };
    } else {
      this.routineState = { ...s, [stepId]: mode };
    }
  }

  @action openNotifyModal(): void {
    // A routine save in flight doesn't touch the contact, but keep the modal gated
    // for consistency with the rest of the in-flight UI. Clear any prior error.
    if (this.isSaving) return;
    this.watchlistContactError = '';
    this.showNotifyModal = true;
  }

  @action closeNotifyModal(): void {
    this.showNotifyModal = false;
  }

  // Writes the envoy-web watch-list contact directly (source of truth), mirroring
  // Security → Watch List: reassign the whole embedded `watchlist` attr, then
  // location.save() (V2Adapter → PATCH /a/visitors/api/v2/locations/:id). The row is
  // company-scoped, so editing via currentLocation writes the one canonical value.
  @action async saveNotifyContacts(email: string, phone: string): Promise<void> {
    const location = this.state.currentLocation;
    if (!location) return;

    const existing = location.watchlist ?? { list: null, notifyEmail: null, notifyPhoneNumber: null };
    // Blanks never overwrite an existing contact — clearing is done on Security →
    // Watch List. Always round-trip the existing `list` unchanged.
    const notifyEmail = email.trim() || existing.notifyEmail;
    const notifyPhoneNumber = phone.trim() || existing.notifyPhoneNumber;
    const list = existing.list;

    // envoy-web deletes the shared watchlist row on an all-blank payload; never send one.
    if (!list && !notifyEmail && !notifyPhoneNumber) {
      this.showNotifyModal = false;
      return;
    }

    this.watchlistContactError = '';
    // Reassign the whole attr — Ember Data won't track in-place mutation of the
    // embedded object. `@attr('immutable')` expects a frozen value.
    location.watchlist = Object.freeze({ list, notifyEmail, notifyPhoneNumber });

    try {
      await location.save();
      this.flashMessages.showAndHideFlash('success', 'Watch list contact updated.');
    } catch (err) {
      location.rollbackAttributes();
      this.watchlistContactError =
        err instanceof AdapterForbiddenError
          ? "You don't have permission to update the watch list contact."
          : 'Failed to update the watch list contact. Please try again.';
    } finally {
      // Close regardless: the inline error renders next to the notify editor on the
      // dashboard, so a failure stays visible instead of being hidden behind the modal.
      this.showNotifyModal = false;
    }
  }

  @action async save(): Promise<void> {
    if (this.saveDisabled) return;
    const groups = this.computeGoingLive();
    if (groups.length) {
      this.pendingDiffGroups = groups;
      this.showSaveConfirmModal = true;
      return;
    }
    await this._commitSave();
  }

  @action async confirmSave(): Promise<void> {
    this.showSaveConfirmModal = false;
    this.pendingDiffGroups = [];
    if (this.saveDisabled) return;
    await this._commitSave();
  }

  @action cancelSave(): void {
    this.showSaveConfirmModal = false;
    this.pendingDiffGroups = [];
  }

  @action cancel(): void {
    // Reverting mid-save would let the completion handler baseline the reverted
    // state as saved; the template also disables the button while isSaving.
    if (this.isSaving) return;
    this.gracePeriodQty = this._savedGraceQty;
    this.gracePeriodUnit = this._savedGraceUnit;
    this.routineState = cloneState(this._savedState);
  }

  private async _commitSave(): Promise<void> {
    if (this.isSaving) return;
    this.isSaving = true;
    try {
      await this._doCommitSave();
    } finally {
      this.isSaving = false;
    }
  }

  private async _doCommitSave(): Promise<void> {
    // Snapshot what we submit BEFORE the request: the form stays editable while
    // the save is in flight (only Save/Cancel/notify-modal are gated), so the
    // payload and the post-response baselines must both come from this snapshot —
    // an edit made mid-flight then correctly stays dirty instead of being
    // silently marked saved without ever reaching the server.
    const submitted = cloneState(this.routineState);
    const submittedGraceQty = this.gracePeriodQty;
    const submittedGraceUnit = this.gracePeriodUnit;

    const multipliers = { minutes: 60, hours: 3600, days: 86400 };
    const gracePeriodSeconds = submittedGraceQty * multipliers[submittedGraceUnit];

    const steps: Array<Record<string, unknown>> = [];

    if (Object.keys(submitted.acs).length) {
      steps.push({
        step_type: 'LOCATION_ACS_DEPROVISION',
        order: 100,
        per_integration_location: Object.entries(submitted.acs).map(([key, mode]) => {
          const [locationId, integrationType] = key.split('|');
          return { location_id: Number(locationId), integration_type: integrationType, mode: toApiMode(mode) };
        }),
      });
    }

    // Watch list step: order 175 places it between ACS (100) and blocklist (200) per
    // the backend registry. The notify contact is NO LONGER carried on the routine
    // snapshot — it lives only in envoy-web (location.watchlist), edited directly by
    // saveNotifyContacts. This step carries the mode only.
    //
    // Org steps still in `unconfigured` are omitted from the payload: that sentinel
    // must never round-trip. Global admins can't reach here with one (saveDisabled
    // gates them until a mode is picked); location-scope admins can't edit org steps,
    // and the backend preserves the prior slice when the body omits a step.
    const watchListIncluded = submitted.watchList !== 'unconfigured';
    if (watchListIncluded) {
      steps.push({
        step_type: 'COMPANY_WATCHLIST_ADD',
        order: 175,
        mode: toApiMode(submitted.watchList),
      });
    }

    if (submitted.blocklist !== 'unconfigured') {
      steps.push({ step_type: 'COMPANY_BLOCKLIST_ADD', order: 200, mode: toApiMode(submitted.blocklist) });
    }
    if (submitted.forceSignout !== 'unconfigured') {
      steps.push({ step_type: 'COMPANY_FORCE_SIGNOUT', order: 300, mode: toApiMode(submitted.forceSignout) });
    }

    const companyId = this.state.currentCompany?.id;
    let response: { rejected_entries?: Array<{ step_type: string; location_id: number | null; reason: string }> };
    try {
      response = await this.ajaxFetch.request(
        `${config.hrisLifecycleApiHost}/a/hris-lifecycle/api/v1/customer-routine-snapshots?company_id=${companyId}&lifecycle_event_type=TERMINATION`,
        {
          method: 'POST',
          data: { schema_version: 2, grace_period_seconds: gracePeriodSeconds, steps },
          contentType: 'application/json',
        },
      );
    } catch (err) {
      if (err instanceof ForbiddenError) {
        this.flashMessages.showAndHideFlash('error', 'You are not authorized to save this configuration.');
      } else {
        this.flashMessages.showAndHideFlash('error', 'Failed to save configuration. Please try again.');
      }
      return;
    }

    const rejected = response.rejected_entries ?? [];
    if (rejected.length === 0) {
      this.flashMessages.showAndHideFlash('success', 'Configuration saved.');
    } else {
      this.flashMessages.showAndHideFlash(
        'warning',
        'Configuration partially saved.',
        `${rejected.length} change${rejected.length === 1 ? '' : 's'} could not be applied — you may not have permission to edit those entries.`,
      );
    }

    // Advance the watch list baseline only when the step was actually accepted:
    // present in this payload (a non-global save omits an unconfigured step —
    // omission is not acceptance) AND absent from rejected_entries.
    //
    // If the backend rejected COMPANY_WATCHLIST_ADD (e.g., permission), the watch
    // list baseline must NOT advance to the submitted picks — otherwise `isDirty`
    // would flip false and the admin loses the retry signal for that step.
    const watchListRejected = rejected.some((r) => r.step_type === 'COMPANY_WATCHLIST_ADD');

    this._savedGraceQty = submittedGraceQty;
    this._savedGraceUnit = submittedGraceUnit;
    const nextSaved = cloneState(submitted);
    if (watchListRejected) {
      // Preserve the pre-save watch list baseline so the user's dirty state persists
      // for their retry attempt on this step alone.
      nextSaved.watchList = this._savedState.watchList;
    }
    this._savedState = nextSaved;

    // `_saved*` baselines aren't tracked, so end with a routineState write to
    // invalidate isDirty consumers — they recompute against the moved baselines.
    this.routineState = { ...this.routineState };
  }
}
